import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useLanguage } from '../../contexts/LanguageContext';
import {
  startFreeTrial,
  submitFreeTrialRequest,
  verifyFreeTrialEmail,
} from '../../api/freeTrialApi';

import '../../styles/FreeTrial/free-trial-request.css';

const BUSINESS_TYPES = [
  'restaurant',
  'cafe',
  'snack',
  'bakery',
  'hotel',
  'riad',
  'other',
];

const LANGUAGES = ['fr', 'ar', 'en', 'es'];


function CustomSelect({
  name,
  value,
  options,
  onChange,
  ariaLabel,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find((option) => option.value === value) ||
    options[0] ||
    null;

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`free-trial-select${open ? ' is-open' : ''}`}
    >
      <button
        type="button"
        className="free-trial-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${name}-options`}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selectedOption?.label || ''}</span>
        <ChevronDown
          className="free-trial-select__chevron"
          size={17}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          id={`${name}-options`}
          className="free-trial-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const selected = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                className={`free-trial-select__option${
                  selected ? ' is-selected' : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span>{option.label}</span>
                {selected ? (
                  <Check size={15} aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function FreeTrialRequest() {
  const { t, language, isRtl } = useLanguage();

  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [verificationSession, setVerificationSession] = useState('');
  const [submissionToken, setSubmissionToken] = useState('');
  const [form, setForm] = useState({
    applicant_name: '',
    phone: '',
    business_name: '',
    business_type: 'restaurant',
    business_email: '',
    business_phone: '',
    business_whatsapp: '',
    business_address: '',
    business_description: '',
    default_language: language || 'fr',
  });

  const progress = useMemo(() => {
    if (step === 'email') return 1;
    if (step === 'verify') return 2;
    if (step === 'business') return 3;
    return 4;
  }, [step]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleStart = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      const result = await startFreeTrial(email, language);

      setRequestId(result.request_id);
      setVerificationSession(result.verification_session || '');
      setForm((current) => ({
        ...current,
        business_email: email,
        default_language: language || 'fr',
      }));
      setStep('verify');
    } catch (requestError) {
      setError(
        requestError.userMessage ||
          t('freeTrial.errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      const result = await verifyFreeTrialEmail({
        request_id: requestId,
        verification_session: verificationSession,
        code,
      });

      setSubmissionToken(result.submission_token || '');
      setStep('business');
    } catch (requestError) {
      setError(
        requestError.userMessage ||
          t('freeTrial.errors.verify'),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      await submitFreeTrialRequest({
        request_id: requestId,
        submission_token: submissionToken,
        ...form,
      });

      setStep('submitted');
    } catch (requestError) {
      setError(
        requestError.userMessage ||
          t('freeTrial.errors.submit'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className={`free-trial-page${isRtl ? ' is-rtl' : ''}`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <section className="free-trial-hero">
        <div className="public-site-container free-trial-hero__inner">
          <span className="free-trial-kicker">
            {t('freeTrial.kicker')}
          </span>
          <h1>{t('freeTrial.title')}</h1>
          <p>{t('freeTrial.description')}</p>
        </div>
      </section>

      <section className="public-site-container free-trial-workspace">
        <aside className="free-trial-steps" aria-label={t('freeTrial.steps.aria')}>
          {[
            ['email', Mail],
            ['verify', ShieldCheck],
            ['business', Building2],
            ['review', CheckCircle2],
          ].map(([key, Icon], index) => {
            const number = index + 1;
            const active = progress === number;
            const complete = progress > number;

            return (
              <div
                key={key}
                className={`free-trial-step${active ? ' is-active' : ''}${
                  complete ? ' is-complete' : ''
                }`}
              >
                <span>
                  {complete ? <Check size={16} /> : <Icon size={17} />}
                </span>
                <div>
                  <small>{t(`freeTrial.steps.${key}.eyebrow`)}</small>
                  <strong>{t(`freeTrial.steps.${key}.title`)}</strong>
                </div>
              </div>
            );
          })}
        </aside>

        <div className="free-trial-card">
          {error ? (
            <div className="free-trial-alert" role="alert">
              {error}
            </div>
          ) : null}

          {step === 'email' ? (
            <form onSubmit={handleStart} className="free-trial-form">
              <div className="free-trial-form__heading">
                <span><Mail size={19} /></span>
                <div>
                  <small>{t('freeTrial.emailStep.eyebrow')}</small>
                  <h2>{t('freeTrial.emailStep.title')}</h2>
                  <p>{t('freeTrial.emailStep.description')}</p>
                </div>
              </div>

              <label className="free-trial-field">
                <span>{t('freeTrial.fields.email')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t('freeTrial.fields.emailPlaceholder')}
                  autoComplete="email"
                  required
                />
              </label>

              <button type="submit" className="free-trial-primary" disabled={busy}>
                {busy ? t('freeTrial.actions.sending') : t('freeTrial.actions.sendCode')}
                {!busy ? <ArrowRight size={17} /> : null}
              </button>
            </form>
          ) : null}

          {step === 'verify' ? (
            <form onSubmit={handleVerify} className="free-trial-form">
              <div className="free-trial-form__heading">
                <span><ShieldCheck size={19} /></span>
                <div>
                  <small>{t('freeTrial.verifyStep.eyebrow')}</small>
                  <h2>{t('freeTrial.verifyStep.title')}</h2>
                  <p>{t('freeTrial.verifyStep.description', { email })}</p>
                </div>
              </div>

              <label className="free-trial-field">
                <span>{t('freeTrial.fields.code')}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  required
                />
              </label>

              <button type="submit" className="free-trial-primary" disabled={busy || code.length !== 6}>
                {busy ? t('freeTrial.actions.verifying') : t('freeTrial.actions.verify')}
                {!busy ? <ArrowRight size={17} /> : null}
              </button>

              <button
                type="button"
                className="free-trial-secondary"
                onClick={() => {
                  setCode('');
                  setError('');
                  setStep('email');
                }}
              >
                {t('freeTrial.actions.changeEmail')}
              </button>
            </form>
          ) : null}

          {step === 'business' ? (
            <form onSubmit={handleSubmit} className="free-trial-form">
              <div className="free-trial-form__heading">
                <span><Building2 size={19} /></span>
                <div>
                  <small>{t('freeTrial.businessStep.eyebrow')}</small>
                  <h2>{t('freeTrial.businessStep.title')}</h2>
                  <p>{t('freeTrial.businessStep.description')}</p>
                </div>
              </div>

              <div className="free-trial-form__grid">
                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.applicantName')}</span>
                  <input
                    name="applicant_name"
                    value={form.applicant_name}
                    onChange={updateField}
                    required
                  />
                </label>

                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.phone')}</span>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={updateField}
                    autoComplete="tel"
                  />
                </label>

                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.businessName')}</span>
                  <input
                    name="business_name"
                    value={form.business_name}
                    onChange={updateField}
                    required
                  />
                </label>

                <div className="free-trial-field">
                  <span>{t('freeTrial.fields.businessType')}</span>
                  <CustomSelect
                    name="business_type"
                    value={form.business_type}
                    ariaLabel={t('freeTrial.fields.businessType')}
                    options={BUSINESS_TYPES.map((type) => ({
                      value: type,
                      label: t(`freeTrial.businessTypes.${type}`),
                    }))}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        business_type: value,
                      }))
                    }
                  />
                </div>

                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.businessEmail')}</span>
                  <input
                    type="email"
                    name="business_email"
                    value={form.business_email}
                    onChange={updateField}
                  />
                </label>

                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.businessPhone')}</span>
                  <input
                    name="business_phone"
                    value={form.business_phone}
                    onChange={updateField}
                  />
                </label>

                <label className="free-trial-field">
                  <span>{t('freeTrial.fields.whatsapp')}</span>
                  <input
                    name="business_whatsapp"
                    value={form.business_whatsapp}
                    onChange={updateField}
                  />
                </label>

                <div className="free-trial-field">
                  <span>{t('freeTrial.fields.defaultLanguage')}</span>
                  <CustomSelect
                    name="default_language"
                    value={form.default_language}
                    ariaLabel={t('freeTrial.fields.defaultLanguage')}
                    options={LANGUAGES.map((locale) => ({
                      value: locale,
                      label: t(`languages.${locale}`),
                    }))}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        default_language: value,
                      }))
                    }
                  />
                </div>

                <label className="free-trial-field free-trial-field--wide">
                  <span>{t('freeTrial.fields.address')}</span>
                  <textarea
                    name="business_address"
                    value={form.business_address}
                    onChange={updateField}
                    rows={3}
                  />
                </label>

                <label className="free-trial-field free-trial-field--wide">
                  <span>{t('freeTrial.fields.description')}</span>
                  <textarea
                    name="business_description"
                    value={form.business_description}
                    onChange={updateField}
                    rows={4}
                  />
                </label>
              </div>

              <button type="submit" className="free-trial-primary" disabled={busy}>
                {busy ? t('freeTrial.actions.submitting') : t('freeTrial.actions.submit')}
                {!busy ? <ArrowRight size={17} /> : null}
              </button>
            </form>
          ) : null}

          {step === 'submitted' ? (
            <div className="free-trial-success">
              <span><CheckCircle2 size={30} /></span>
              <small>{t('freeTrial.success.eyebrow')}</small>
              <h2>{t('freeTrial.success.title')}</h2>
              <p>{t('freeTrial.success.description')}</p>
              <div>
                <strong>{email}</strong>
                <span>{t('freeTrial.success.note')}</span>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
